import { ACTIVITY_BOARD_PERMISSION_CODES } from './activity-board-permissions';

type PublicRouteDefinition = {
  path: string;
};

type StandaloneRouteDefinition = {
  basePath: string;
  paths: readonly string[];
};

type PrivateRouteDefinition = {
  path: string;
  anyOfPermissions: readonly string[];
};

export const ROOT_ROUTE_PATH = '/';
export const FALLBACK_ROUTE_PATH = '/*';

export const PUBLIC_ROUTE_REGISTRY = {
  login: { path: '/login' },
  signUp: { path: '/sign-up' },
  forgotPassword: { path: '/forgot-password' },
} as const satisfies Record<string, PublicRouteDefinition>;

export type PublicRouteId = keyof typeof PUBLIC_ROUTE_REGISTRY;

export const STANDALONE_ROUTE_REGISTRY = {
  confirmEffort: {
    basePath: '/confirm-effort',
    paths: ['/confirm-effort/:guid'],
  },
  ticketSurvey: {
    basePath: '/ticket/survey',
    paths: ['/ticket/survey/:uniqueKey?', '/ticket/survey/*'],
  },
} as const satisfies Record<string, StandaloneRouteDefinition>;

export type StandaloneRouteId = keyof typeof STANDALONE_ROUTE_REGISTRY;

export const PRIVATE_ROUTE_REGISTRY = {
  projectStepManaging: {
    path: '/project-step-managing',
    anyOfPermissions: ['list-project-task'],
  },
  projectPlanGenerate: {
    path: '/project-plan-generate',
    anyOfPermissions: ['add-project-task-list'],
  },
  customerSourceTypes: {
    path: '/customer-source-types',
    anyOfPermissions: ['list-customer-source-type'],
  },
  effortStatusContacts: {
    path: '/effort-status-contacts',
    anyOfPermissions: ['list-effort-status-contact'],
  },
  freshdeskFeedbackReport: {
    path: '/freshdesk-feedback-report',
    anyOfPermissions: ['report-freshdesk'],
  },
  users: {
    path: '/users',
    anyOfPermissions: ['list-user'],
  },
  roles: {
    path: '/roles',
    anyOfPermissions: ['list-role'],
  },
  report: {
    path: '/report',
    anyOfPermissions: ['report-page-view'],
  },
  book: {
    path: '/book',
    anyOfPermissions: ['create-book'],
  },
  profile: {
    path: '/profile',
    anyOfPermissions: [],
  },
  projects: {
    path: '/projects',
    anyOfPermissions: ['list-product'],
  },
  timeline: {
    path: '/timeline',
    anyOfPermissions: ['timeline-page-view'],
  },
  gibTypes: {
    path: '/gib-types',
    anyOfPermissions: ['list-gib-method'],
  },
  dashboard: {
    path: '/dashboard',
    anyOfPermissions: [],
  },
  usedBanks: {
    path: '/used-banks',
    anyOfPermissions: ['list-bank'],
  },
  generalReport: {
    path: '/general-report',
    anyOfPermissions: ['report-page-view'],
  },
  activityTypes: {
    path: '/activity-types',
    anyOfPermissions: ['list-activity-type'],
  },
  effortTracing: {
    path: '/effort-tracing',
    anyOfPermissions: ['list-effort'],
  },
  productReport: {
    path: '/product-report',
    anyOfPermissions: ['report-page-view'],
  },
  projectGroups: {
    path: '/project-groups',
    anyOfPermissions: ['list-product-group'],
  },
  effortTimeline: {
    path: '/effort-timeline',
    anyOfPermissions: ['list-effort-timeline'],
  },
  effortContacts: {
    path: '/effort-contacts',
    anyOfPermissions: ['list-effort-contact'],
  },
  connectionTypes: {
    path: '/connection-types',
    anyOfPermissions: ['list-connection-type'],
  },
  salesStageTypes: {
    path: '/sales-stage-types',
    anyOfPermissions: ['list-sales-stage-type'],
  },
  personnelHoliday: {
    path: '/personnel-holiday',
    anyOfPermissions: ['list-holiday'],
  },
  environmentTypes: {
    path: '/environment-types',
    anyOfPermissions: ['list-environment-type'],
  },
  activityManaging: {
    path: '/activity-managing',
    anyOfPermissions: ['list-activity-board-item', 'list-activity-board-item-admin'],
  },
  holidayMonitoring: {
    path: '/holiday-monitoring',
    anyOfPermissions: [ACTIVITY_BOARD_PERMISSION_CODES.admin],
  },
  projectMonitoring: {
    path: '/project-monitoring',
    anyOfPermissions: ['list-project-report'],
  },
  holidayYearlyView: {
    path: '/holiday-yearly-view',
    anyOfPermissions: [ACTIVITY_BOARD_PERMISSION_CODES.admin],
  },
  freshdeskDashboard: {
    path: '/freshdesk-dashboard',
    anyOfPermissions: ['freshdesk'],
  },
  activityMonitoring: {
    path: '/activity-monitoring',
    anyOfPermissions: ['get-activity-monitoring'],
  },
  productProjectPlan: {
    path: '/product-project-plan',
    anyOfPermissions: ['list-project-task-base'],
  },
} as const satisfies Record<string, PrivateRouteDefinition>;

export type PrivateRouteId = keyof typeof PRIVATE_ROUTE_REGISTRY;
